import { invokeDsfTable, streamDsfTable } from "@dashai/lib/dsfTableClient";

// `invokeDsfTable` calls the dashaiDsfTable Firebase function and returns a
// `{ data, error }` envelope, destructured in `invokeMode` below.

async function invokeMode(mode, body) {
  const { data, error } = await invokeDsfTable({ mode, ...body });
  if (error) throw error;
  if (!data?.success) {
    throw new Error(data?.error || `${mode} request failed`);
  }
  return data;
}

// Whether a failed request is the server refusing the caller's country grant.
// `invokeDsfTable` carries the HTTP status and the response body as the message.
const isModuleScopeDenied = (err) =>
  err?.status === 403 && String(err?.message || "").includes("module_scope_denied");

// Streaming variant for endpoints that return a non-JSON CSV body.
// `streamDsfTable` returns the raw Response so we can call `.blob()` to get
// a true streamed download without a JSON intermediate.
async function invokeModeBlob(mode, body) {
  const response = await streamDsfTable({ mode, ...body });
  return response.blob();
}

// Overview tab. Server-side ROW pagination (fixed rows/page) ranked by franchise store count
// DESC over the full (filtered) franchise universe. Default path passes page/pageSize; the
// bounded `franchises` selection (in-table filter, handful of names) switches to the backend's
// franchises[] path so a selection is never cut off by a row-page boundary — the unbounded
// 5000-franchise IN-list failure stays impossible because the list only ever holds the user's
// explicit picks. Returns { success, rows, clientCompanies, totalCount, page, pageSize, hasMore }
// (no envelope on the franchises[] path).
export const fetchKeyAccountsOverview = ({
  id_client,
  country,
  refresh,
  page = 1,
  pageSize = 50,
  sortBy,
  segment,
  onlyWithBrand,
  splitByPlatform = true,
  // Optional bounded franchise selection (the in-table filter) — restricts the paged universe
  // server-side, so segment / brand-toggle / split / row-pagination all keep applying and the
  // response keeps its pagination envelope.
  franchises,
}) =>
  invokeMode("multi-franchise-gaps", {
    id_client,
    country,
    refresh,
    page,
    pageSize,
    ...(Array.isArray(franchises) && franchises.length > 0 ? { franchises } : {}),
    ...(sortBy ? { sortBy } : {}),
    ...(segment && segment !== "all" ? { segment } : {}),
    ...(onlyWithBrand ? { only_with_brand: true } : {}),
    ...(splitByPlatform === false ? { split_by_platform: false } : {}),
  });

// Whole-table CSV export for the Overview tab — streams the FULL filtered franchise universe
// (segment / brand-toggle / platform-split all honored), not just the loaded page. Returns a
// Blob ready for a download <a>.
export const fetchKeyAccountsOverviewCsv = ({
  id_client,
  country,
  refresh,
  segment,
  onlyWithBrand,
  splitByPlatform = true,
  platforms,
  franchises,
}) =>
  invokeModeBlob("multi-franchise-gaps-export", {
    id_client,
    country,
    refresh,
    isExport: true,
    ...(segment && segment !== "all" ? { segment } : {}),
    ...(onlyWithBrand ? { only_with_brand: true } : {}),
    ...(splitByPlatform === false ? { split_by_platform: false } : {}),
    ...(Array.isArray(platforms) && platforms.length > 0 ? { platforms } : {}),
    ...(Array.isArray(franchises) && franchises.length > 0 ? { franchises } : {}),
  });

// Details tab. Drill-down mode — caller passes a single franchise and gets back a paginated
// slice of its stores + the SKU compliance matrix restricted to those stores. The single-
// franchise path is mandatory: fetching all top-N franchises at once OOM'd the worker for
// high-volume workspaces (Subway alone has ~38K US stores). Even a single big chain (CVS US
// at ~10K stores) blows the worker without store-level pagination, hence page/pageSize.
//
// platform / search apply server-side so they survive across pages — client-side filters
// would only ever see the current page.
export const fetchKeyAccountsDetails = ({
  id_client,
  country,
  refresh,
  franchises,
  page = 1,
  pageSize = 25,
  platform,
  search,
  sortBy,
  // Restaurant/Retail radio — restricts the store universe server-side ("all" is stripped).
  segment,
  // Server-side column filters: { isClientListed?, hasCombo?, hasComboFixed?,
  // hasComboOption?, skus? } — skus matches stores listing ANY of the selected SKU ids.
  // Server-side so they span every page, like platform/search.
  columnFilters,
}) =>
  invokeMode("multi-franchise-compliance", {
    id_client,
    country,
    refresh,
    franchises,
    page,
    pageSize,
    ...(platform ? { platform } : {}),
    ...(search ? { search } : {}),
    ...(segment && segment !== "all" ? { segment } : {}),
    // Server-side column sort so ordering spans all pages, not just the visible one. Omitted
    // when null → backend keeps its default store-name ordering.
    ...(sortBy ? { sortBy } : {}),
    ...(columnFilters && Object.keys(columnFilters).length > 0 ? { columnFilters } : {}),
  });

// Outlet view: platform listings collapsed into MATCHED physical outlets
// (id_physical_outlet), one row per outlet with availability per platform.
// Returns { rows: [{ outletId, storeName, storeCity, storeAddress, storeUrl,
// isAvailable, platformAvailability: { CODE: bool } }], platforms, totalCount }.
export const fetchKeyAccountsOutlets = ({
  id_client,
  country,
  refresh,
  franchises,
  page = 1,
  pageSize = 25,
  search,
  segment,
}) =>
  invokeMode("outlet-matched", {
    id_client,
    country,
    refresh,
    franchises,
    page,
    pageSize,
    ...(search ? { search } : {}),
    ...(segment && segment !== "all" ? { segment } : {}),
  });

// Outlet-view Overview: franchises ranked by matched physical outlets, with
// Availability / Assortment / Combo per platform.
// Returns { rows: [{ franchiseName, segment, outlets, platforms: { [platform]:
// { onPlatform, available, assortment, combo } } }], platforms, totalCount }.
export const fetchKeyAccountsOutletOverview = ({
  id_client,
  country,
  refresh,
  page = 1,
  pageSize = 50,
  franchises,
  segment,
  onlyWithBrand,
  sortBy,
}) =>
  invokeMode("outlet-overview", {
    id_client,
    country,
    refresh,
    page,
    pageSize,
    ...(sortBy ? { sortBy } : {}),
    ...(Array.isArray(franchises) && franchises.length > 0 ? { franchises } : {}),
    ...(segment && segment !== "all" ? { segment } : {}),
    ...(onlyWithBrand ? { only_with_brand: true } : {}),
  });

// Full-table CSV of the Outlets overview (every page, current filters), one row
// per franchise × type × platform. Returns a Blob (text/csv).
export const fetchKeyAccountsOutletOverviewCsv = ({
  id_client,
  country,
  refresh,
  segment,
  onlyWithBrand,
  franchises,
  sortBy,
}) =>
  invokeModeBlob("outlet-overview-export", {
    id_client,
    country,
    refresh,
    isExport: true,
    ...(segment && segment !== "all" ? { segment } : {}),
    ...(onlyWithBrand ? { only_with_brand: true } : {}),
    ...(Array.isArray(franchises) && franchises.length > 0 ? { franchises } : {}),
    ...(sortBy ? { sortBy } : {}),
  });

// Outlet-overview franchise picker: the top franchises by matched outlets, or
// those matching `search`. Returns { franchises: [{ name, outlets }] }.
export const fetchKeyAccountsOutletFranchiseNames = ({
  id_client,
  country,
  refresh,
  segment,
  search,
}) =>
  invokeMode("outlet-franchise-names", {
    id_client,
    country,
    refresh,
    ...(segment && segment !== "all" ? { segment } : {}),
    ...(search ? { search } : {}),
  });

// Outlet-view Over Time: matched-outlet counts per (franchise, refresh).
// Returns { rows: [{ franchiseName, refresh, segment, outlets, selling }] }.
export const fetchKeyAccountsOutletTrend = ({
  id_client,
  country,
  refresh,
  franchises,
  refreshes,
  segment,
}) =>
  invokeMode("outlet-trend", {
    id_client,
    country,
    refresh,
    franchises,
    refreshes,
    ...(segment && segment !== "all" ? { segment } : {}),
  });

// Full-franchise CSV export. Returns a Blob (text/csv) ready to wire into a download <a>.
// The edge function streams `FORMAT CSVWithNames` from ClickHouse, so this is safe even on
// big chains (CVS US ~19K stores, Subway US ~38K). Respects the same platform / search
// filters the on-screen Details tab applies — the CSV reflects what the user is viewing.
export const fetchKeyAccountsDetailsCsv = ({
  id_client,
  country,
  refresh,
  franchises,
  platform,
  search,
  segment,
  columnFilters,
}) =>
  invokeModeBlob("multi-franchise-compliance-export", {
    id_client,
    country,
    refresh,
    franchises,
    ...(platform ? { platform } : {}),
    ...(search ? { search } : {}),
    ...(segment && segment !== "all" ? { segment } : {}),
    // Same column filters as the on-screen table — the exported file must match the view.
    ...(columnFilters && Object.keys(columnFilters).length > 0 ? { columnFilters } : {}),
  });

// Searchable, paginated franchise lookup for the dashboard's franchise filter dropdown. Ranked
// by store count DESC (so users see Subway/Starbucks/McDonald's at the top, not "$3.99 PIZZA CO"
// at the top from the previous alphabetical ordering). `search` is a case-insensitive substring
// match — pass user-typed input as the user types (debounced upstream).
export const fetchKeyAccountsFranchises = async ({
  id_client,
  country,
  offset = 0,
  limit = 200,
  search,
  segment,
  // Optional platform restriction — scopes both the name list and the per-franchise store
  // counts, so the dropdown's bracketed counts shrink when a platform filter is active.
  platform,
  // Optional refresh — pins the store counts to the refresh the page is showing so the
  // bracketed numbers agree with the tables next to them. Omitted → backend uses the latest.
  refresh,
  // Optional "only selling our brand" restriction (Over Time dropdown) — server-side, same
  // HAVING as the Overview universe, so the option list itself shrinks.
  onlyWithBrand,
}) => {
  const data = await invokeMode("franchise-names", {
    id_client,
    country,
    offset,
    limit,
    ...(search ? { search } : {}),
    ...(segment && segment !== "all" ? { segment } : {}),
    ...(platform ? { platform } : {}),
    ...(refresh ? { refresh } : {}),
    ...(onlyWithBrand ? { only_with_brand: true } : {}),
  });
  return {
    franchises: Array.isArray(data?.franchises) ? data.franchises : [],
    // Additive counts payload; empty on cached responses that predate it — callers fall back
    // to count-less labels.
    franchiseCounts: Array.isArray(data?.franchiseCounts) ? data.franchiseCounts : [],
    totalCount: Number(data?.totalCount) || 0,
    hasMore: Boolean(data?.hasMore),
  };
};

// What the workspace+country holds, in both shapes the page needs: `refreshes` newest-first for
// the header selector, and `periods` keyed by grain for the Over Time range control. The server
// groups the refreshes into periods, so the browser never works the grain rule out for itself.
// `withCompanies` also asks for `brandLabel`, the name the combined page gives
// the client in its copy, or null when the market names no company, and for
// `comboSplits`, the names of the brand's combo splits.
// dashaiDsfTable resolves the latest refresh before it runs any mode, so a market
// without data answers with a 404. That answer means the market holds no months,
// so it returns an empty list rather than an error.
// The list describes the market, so the server serves it to a caller holding
// either the Execution View or the Physical Franchise grant for the country.
// A caller holding neither gets a 403, which returns `denied: true` so the
// page can say so instead of offering a retry.
export const fetchKeyAccountsRefreshes = async ({ id_client, country, withCompanies = false }) => {
  let data;
  let denied = false;
  try {
    data = await invokeMode("refresh-list", {
      id_client,
      country,
      ...(withCompanies ? { with_companies: true } : {}),
    });
  } catch (err) {
    denied = isModuleScopeDenied(err);
    if (err?.status !== 404 && !denied) throw err;
    data = null;
  }
  return {
    denied,
    refreshes: Array.isArray(data?.refreshes) ? data.refreshes : [],
    periods: data?.periods && typeof data.periods === "object" ? data.periods : {},
    brandLabel: typeof data?.brandLabel === "string" && data.brandLabel ? data.brandLabel : null,
    comboSplits: Array.isArray(data?.comboSplits) ? data.comboSplits : [],
  };
};

// One row per (franchise, period, segment, platform) for up to 5 franchises across the provided
// refreshes, pooled to `period` ('month' default, 'quarter', 'year'). Backs the Over Time tab's
// chart + table. Every row, brand-trend row and price-trend row carries `refresh` (the period
// key: its first month), `refresh_count`, `expected_refresh_count` and `is_complete`; the
// response echoes `period`.
// `brandGrain` cuts the brand series at brand or subbrand level — "Red Bull"
// versus "Red Bull Sugarfree". Omitted (the default) keeps brand grain, so
// existing callers are unaffected.
export const fetchKeyAccountsFranchiseTrend = ({
  id_client,
  country,
  franchises,
  refreshes,
  brandGrain,
  period,
}) =>
  invokeMode("franchise-trend", {
    id_client,
    country,
    franchises,
    refreshes,
    ...(brandGrain === "subbrand" ? { brandGrain } : {}),
    ...(period && period !== "month" ? { period } : {}),
  });

// ---------------------------------------------------------------------------
// Global Key Accounts — allowlisted cross-country modes. All three
// omit `country`: the backend scopes every country to its own latest refresh.
// ---------------------------------------------------------------------------

// The countries the caller may open on the Key Accounts page, each with its
// name and the views it offers: `[{ country, name, views: { dsf, outlet } }]`.
// The server decides both from the grants its per-country modes enforce, and
// lists no country the caller holds neither grant for.
export const fetchKeyAccountsCountries = async ({ id_client }) => {
  const data = await invokeMode("outlet-access-check", { id_client });
  if (!Array.isArray(data?.countries)) return [];
  return data.countries
    .filter((c) => typeof c?.country === "string" && c.country)
    .map((c) => ({
      country: c.country,
      name: typeof c.name === "string" && c.name ? c.name : c.country,
      views: { dsf: c.views?.dsf === true, outlet: c.views?.outlet === true },
    }));
};

// Which Global views the caller may open: `{ dsf, outlet }`, each true when the
// caller holds a country in that view. A denial (403 body) resolves both false
// instead of throwing; other errors still throw (the caller falls back to hidden).
export const fetchGlobalKeyAccountsViews = async ({ id_client }) => {
  const none = { dsf: false, outlet: false };
  const { data, error } = await invokeDsfTable({ mode: "global-access-check", id_client });
  if (error) {
    // invokeDsfTable surfaces non-OK responses as Error(bodyText) — a denial
    // carries the backend's stable error code in that body.
    if (String(error.message || "").includes("global_key_accounts_not_allowed")) return none;
    if (String(error.message || "").includes("HTTP 403")) return none;
    throw error;
  }
  if (data?.success !== true || data?.allowed !== true) return none;
  return { dsf: data.views?.dsf === true, outlet: data.views?.outlet === true };
};

// Capability probe backing the nav/route/Cmd-K gating of the standalone Global
// Key Accounts page. That page reads only DSF data, so it needs a DSF country.
// Resolves false on a denial; other errors still throw.
export const fetchGlobalKeyAccountsAccess = async ({ id_client }) =>
  (await fetchGlobalKeyAccountsViews({ id_client })).dsf;

// Top-25 franchises per segment worldwide (>= 3 selling countries), with the
// per-country selling/stores/gap matrix. Returns { franchises, countries, criteria }.
export const fetchGlobalKeyAccountsFranchises = ({ id_client }) =>
  invokeMode("global-franchises", { id_client });

// The grain the rows pool to. How many refreshes that costs per country is the
// backend's call, since it is the one paying for the scan: it multiplies the
// points it draws by the months a period of this grain spans.
const globalPeriodFields = (period) => (period && period !== "month" ? { period } : {});

// One franchise compared across countries, pooled to `period`. Returns
// { franchise, period, periods, countries: [{ country, refreshes }],
// rows: [{ country, refresh, segment, stores, selling, combo, image,
// assortment, gap, refresh_count, expected_refresh_count, is_complete }],
// partialFailures? }. `refresh` is the period key (its first month);
// `countries` lists the refreshes scanned per country, from which the page
// derives the periods on offer and the completeness of each.
// `assortment` (avg unique subbrands per selling store) comes from a
// portfolio-side query the other counters don't need, and is null wherever it
// could not be derived. `partialFailures` names any metric the backend had to
// drop, so the UI can distinguish "the franchise has none" from "we couldn't
// compute it" — both of which otherwise render as an empty chart.
export const fetchGlobalKeyAccountsFranchiseTrend = ({ id_client, franchise, segment, period }) =>
  invokeMode("global-franchise-trend", {
    id_client,
    franchise,
    ...(segment && segment !== "all" ? { segment } : {}),
    ...globalPeriodFields(period),
  });

// One brand (or subbrand) for one franchise, compared across countries. Returns
// { franchise, brandGrain, brand, period, periods, countries, options: [{ brand,
// stores }], rows: [{ country, refresh, segment, stores, refresh_count,
// expected_refresh_count, is_complete }] }, with `refresh` the period key.
//
// The picker is server-ranked, not client-filtered: this is the competitor view,
// so it carries no company filter and the candidate list is far too large to ship
// whole. `options` is what the backend ranked; `brand` is what it actually
// charted, which is the caller's pick only when that pick survived the ranking —
// so callers should follow the response rather than assume their request won.
export const fetchGlobalKeyAccountsBrandTrend = ({
  id_client,
  franchise,
  segment,
  brandGrain,
  brand,
  period,
}) =>
  invokeMode("global-brand-trend", {
    id_client,
    franchise,
    ...(segment && segment !== "all" ? { segment } : {}),
    ...(brandGrain === "subbrand" ? { brandGrain } : {}),
    ...(brand ? { brand } : {}),
    ...globalPeriodFields(period),
  });
